import React from 'react'
import classNames from 'classnames'
import Badge from '@toasttab/shared/src/components/atoms/Badge/Badge'
import { Heading } from '@toasttab/shared/src/components'
import Description from '@toasttab/shared/src/components/atoms/Description/Description'
import Eyebrow from '@toasttab/shared/src/components/molecules/Eyebrow/Eyebrow'
import GridCard from '@toasttab/shared/src/components/organisms/Grid/GridCard/GridCard'
import GridColumn from '@toasttab/shared/src/components/organisms/Grid/GridColumn/GridColumn'
import GridLayout from '@toasttab/shared/src/components/organisms/Grid/GridLayout/GridLayout'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import ToastTips from '@toasttab/otl/src/components/Article/ToastTips'
import { getImage } from '@toasttab/shared/src/utils/siteUtils'
import { FeaturedArticle } from '../FeaturedArticle/FeaturedArticle.d'
import { RelatedArticle, RelatedArticlesProps } from './RelatedArticles.d'

const RelatedArticlesModule = ({
  heading,
  scrollingMode = 'normal',
  relatedArticles,
  layout = 'horizontal',
  fullWidth = true,
  displayAsTrendingArticles
}: RelatedArticlesProps) => {
  const { region, prefix } = useI18next()

  const sectionCss = classNames('otl-related-articles z-10 mb-6', {
    'relative h-full w-full': scrollingMode === 'sticky'
  })

  const containerCss = classNames(
    'border-t border-b border-gray-30 md:rounded-lg py-6 lg:p-10',
    {
      'w-full': fullWidth,
      'w-full md:w-3/5': !fullWidth
    }
  )

  const stickyContainerCss = classNames({
    'md:sticky md:top-44 bg-white h-auto': scrollingMode === 'sticky'
  })

  const renderArticle = (
    article: RelatedArticle | FeaturedArticle,
    key: number,
    layout = 'horizontal',
    last: boolean
  ) => {
    const getEyebrowContent = (article: RelatedArticle | FeaturedArticle) => {
      return article.onTheLineTopic && article.onTheLineTopic.length > 0
        ? article.onTheLineTopic[0].title
        : 'ARTICLE'
    }

    if (layout === 'vertical') {
      return (
        <GridCard
          classes='border border-solid border-otl-gray-light2 z-10'
          key={key}
          url={`${prefix}/${article.slug}`}
          image={
            article.entryImages && article.entryImages[0]?.thumbnailImage[0]
          }
          layout='vertical'
          imageDirection={'left'}
          arrowHover
          shadowHover
          scaleImageHover={false}
          imageSize='md'
        >
          <div className='p-4 pt-0'>
            <Eyebrow variation='otl-grey'>{getEyebrowContent(article)}</Eyebrow>
            <Heading
              heading='h3'
              content={article.heroTitle ?? article.title}
              classes='mb-3'
              color={['text-default']}
              fontWeight='font-semibold'
              fontSize={['text-3xl']}
              textAlign={['text-left']}
              fontFamily='font-calibre'
              hoverEffect='headline__text-otl-orange'
            />
            <Description variation='otl-light-xl'>
              {article.shortDescription ?? article.sectionDescription}
            </Description>
          </div>
        </GridCard>
      )
    }

    const getImage = () => {
      if (article.multiPartContentBuilder2) {
        const textImage = article.multiPartContentBuilder2.filter(
          (x) => x.image
        )[0]
        return textImage?.image[0]
      }
      return article.entryImages && article.entryImages[0]?.thumbnailImage[0]
    }

    const cardCss = classNames({
      'h-36 pl-10': !getImage(),
      'py-4 mb-4 border-b-[1px] border-solid border-otl-gray-light2': !last,
      'pt-4': last
    })

    return (
      <GridCard
        classes={cardCss}
        key={key}
        url={`${prefix}/${article.slug}`}
        image={getImage()}
        layout='horizontal'
        imageDirection={'left'}
        arrowHover={false}
        shadowHover={false}
        scaleImageHover={false}
        imageSize='xxs'
        horizScale='20/80'
        horizWrap={false}
        variation={'otl-related-articles-item'}
      >
        {!getImage() && (
          <>
            <ToastTips size='small' />
            <div className='px-5'>
              <Eyebrow variation='otl-grey'>
                {getEyebrowContent(article)}
              </Eyebrow>
              <Heading
                heading='h4'
                content={article.heroTitle ?? article.title}
                classes='leading-tight'
                color={['text-default']}
                fontWeight='font-semibold'
                fontSize={['text-xl']}
                textAlign={['text-left']}
                fontFamily='font-calibre'
                hoverEffect='headline__text-otl-orange'
              />
            </div>
          </>
        )}
        {getImage() && (
          <>
            <Eyebrow variation='otl-related-articles'>
              {getEyebrowContent(article)}
            </Eyebrow>
            <Heading
              heading='h4'
              content={article.heroTitle ?? article.title}
              classes='leading-tight'
              color={['text-black']}
              fontWeight='font-normal'
              fontSize={['text-base']}
              textAlign={['text-left']}
              fontFamily='font-sansPro'
              hoverEffect='headline__text-otl-orange'
            />
          </>
        )}
      </GridCard>
    )
  }

  return (
    <>
      {relatedArticles && relatedArticles.length > 0 && (
        <section className={sectionCss}>
          <div className={stickyContainerCss}>
            <div className={containerCss}>
              {displayAsTrendingArticles && (
                <Badge
                  text='Trending'
                  uppercase={true}
                  badgeColor='bg-lavender-25'
                  classes='mb-1 font-sansPro text-xs tracking-[1.2px]'
                  icon='schedule'
                />
              )}
              {heading && (
                <Heading
                  heading='h4'
                  content={heading}
                  classes='mb-4'
                  color={['text-default']}
                  fontWeight='font-normal'
                  fontSize={['text-2.5xl']}
                  textAlign={['text-left']}
                  fontFamily='font-effra'
                  leading={null}
                />
              )}
              <GridLayout
                column='grid-cols-1'
                gap={layout === 'horizontal' ? 'sm' : 'md'}
              >
                {relatedArticles?.map(
                  (article: RelatedArticle | FeaturedArticle, i: number) => {
                    const art: any = article
                    let entry = art.regions.find(
                      (x: any) => x.country === region
                    )
                    if (!entry) {
                      return <></>
                    }

                    const thumbnailImage = entry.thumbnailImage
                      ? getImage(entry.thumbnailImage)
                      : null
                    article.onTheLineTopic = entry.onTheLineTopic
                    article.entryImages = [
                      { heroImage: null, thumbnailImage: [thumbnailImage] }
                    ]
                    let last = relatedArticles.length - 1 === i
                    return (
                      <GridColumn
                        columnSpan={['col-span-1']}
                        classes=''
                        key={i}
                      >
                        {renderArticle(article, i, layout, last)}
                      </GridColumn>
                    )
                  }
                )}
              </GridLayout>
            </div>
          </div>
        </section>
      )}
    </>
  )
}

export default RelatedArticlesModule
